import { TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { InfluencerData } from "@/features/influencer-monitoring/store/influencerStore";
import { PLATFORM_NAMES, PLATFORM_KEYS } from "@/constants/platformData";

interface TableHeaderProps {
  sortColumn: keyof InfluencerData | null;
  sortDirection: "asc" | "desc" | null;
  onSort: (column: keyof InfluencerData) => void;
  enabledPlatforms?: string[];
}

export function InfluencersTableHeader({
  sortColumn,
  sortDirection,
  onSort,
  enabledPlatforms = [],
}: TableHeaderProps) {
  const getSortIcon = (column: keyof InfluencerData) => {
    if (sortColumn !== column) {
      return <i className="ri-arrow-up-s-fill text-md opacity-50"></i>;
    }

    if (sortDirection === "asc") {
      return <i className="ri-arrow-up-s-fill text-md"></i>;
    }

    return <i className="ri-arrow-down-s-fill text-md"></i>;
  };

  // Map platform keys to their standard names for comparison
  const isPlatformDisabled = (platformKey: keyof InfluencerData) => {
    if (enabledPlatforms.length === 0) return false;

    // Map internal keys to API platform names
    const platformMap: Record<string, string> = {
      facebook: "facebook",
      twitter: "twitter",
      instagram: "instagram",
      reddit: "reddit",
      yt: "youtube",
      tiktok: "tiktok",
    };

    const apiPlatformName = platformMap[platformKey];
    return !enabledPlatforms.includes(apiPlatformName);
  };

  return (
    <TableHeader className="sticky top-0 z-10 bg-neutral-100">
      <TableRow className="bg-neutral-100">
        {PLATFORM_NAMES.map((platform, index) => {
          const isDisabled = isPlatformDisabled(PLATFORM_KEYS[index]);
          return (
            <TableHead
              key={platform}
              className={cn(
                "text-md max-w-[180px] min-w-[140px] p-3 font-bold transition-colors",
                isDisabled
                  ? "cursor-not-allowed text-neutral-400"
                  : "cursor-pointer text-neutral-950 hover:bg-neutral-200",
                index === 0 && "rounded-tl-[12px]",
                index === PLATFORM_NAMES.length - 1 && "rounded-tr-[12px]",
              )}
              onClick={() => !isDisabled && onSort(PLATFORM_KEYS[index])}
            >
              <div className="flex items-center gap-2">
                {/* TEXT */}
                <span className="min-w-0 flex-1 truncate">{platform}</span>

                {/* ICON */}
                {!isDisabled && (
                  <span className="flex-shrink-0">
                    {getSortIcon(PLATFORM_KEYS[index])}
                  </span>
                )}
              </div>
            </TableHead>
          );
        })}
      </TableRow>
    </TableHeader>
  );
}
